import { CommonUtils } from "../../(common-lib)/CommonUtils";
import { LoanAppData } from "../../(common-lib)/LoanAppData";
import { LoanStatus, type DocumentUploaded } from "../../(common-lib)/LoanData";
import { parseJsonArray } from "../../(common-lib)/Utils";
import { ApiCache } from "../../(ui-lib)/ApiCache";
import { FormDataUtils } from "../../(ui-lib)/FormDataUtils";
import { GUARANTOR_INFORMATION_SECTION_ID } from "./ParticipantGuarantors";
import { normalizeCustomFieldId } from "./Utils";
import { ApplicationField, ApplicationSection, 
        SectionGroup, isSectionGroup} from "./ApplicationTypes";
import { DocumentStatus } from "./Document";
import { pickGovtIssuedIdDoc, removeGovtIssuedIdDoc, storeGovtIssuedIdDoc } from "./GovtIssuedIdDoc";
import { readThirdPartyReportField, writeThirdPartyReportField } from "./ThirdPartyReports";
import {
  copyLiquidityAccountsToBorrowerInformation,
  getLiquidityAccountEntries,
  LIQUIDITY_ACCOUNT_TYPE_FIELD_ID,
  LIQUIDITY_MOST_RECENT_DOC_ID,
  LIQUIDITY_PREVIOUS_DOC_ID,
  LIQUIDITY_STORAGE_FIELD_ID,
} from "./Liquidity";
import { initializeSOWData, normalizeSOWData, filterSOWData } from "../sow/sow";
import type { SOWData } from "../sow/sow/sow.types";

//
// Application: is the core logic hub
//      - section filtering, field/doc visibility, status computation, and bidirectional copy between form state and loan data.
//      - Reads the section-field to loan parameters mapping data. Based on the current loan data and returns a filtered section-field list
//      - Give the list of Section - Fields UI data
//
// All these use Application
// PortalContext: Orchestrates data loading and initializes the UI state (sections, field/doc maps).
// ├── ApplicationData
// ApplicationContent: Renders a selected section, defers to Field/Document inputs, and persists via StorageClient.
// ├── ApplicationFieldInput [Renders fields; uses fieldIdToValue; calls onFieldUpdate]
// ├── ApplicationDocumentInput [Per-doc UI; uploads/removes; via DocumentMappingService]
//
// Static configuation of section-field to loan parameters (Loan Purpose, Exit Strategy, Property Type) Mapping
// ApiCache (getApplicationSections) --> api/config --> ApplicationFieldService --> application-section-fields.csv

// ---------------------------------------------------------------------------------------
// CONDITONAL DISPLAY LOGIC
// ---------------------------------------------------------------------------------------
// Whether a field should be shown or not - based on the value of a specific field
export function isDisplayConditionTrue(fieldOrDocOrSectionId: string, fieldIdToValue: Record<string, string>): boolean {
  const suffixIndex = fieldOrDocOrSectionId.indexOf('__');
  const suffix = suffixIndex === -1 ? '' : fieldOrDocOrSectionId.slice(suffixIndex);
  const baseId = suffixIndex === -1 ? fieldOrDocOrSectionId : fieldOrDocOrSectionId.slice(0, suffixIndex);

  switch (fieldOrDocOrSectionId) {
    // DOCUMENTS
    case "rent_roll_opex_doc":
    case "in_place_leases_doc":
      return fieldIdToValue["tenant_occupied"] === "Yes";
    case "contract_assignment_doc":
      return Number(fieldIdToValue["assignment_fee"]) > 0;

    // SECTIONS
    case "upload_active_building_permit":
    case "upload_building_plans": {
      const exitStrategy = fieldIdToValue["loan_type"];
      if (exitStrategy === "New Construction") return true;
      if (exitStrategy !== "Renovation" && exitStrategy !== "Renovation / Fix & Flip") return false;

      const currentSqft = Number(fieldIdToValue["project_above_current_sqft"]);
      const rehabbedSqft = Number(fieldIdToValue["project_above_rehab_sqft"]);
      if (!Number.isFinite(currentSqft) || !Number.isFinite(rehabbedSqft)) return false;

      return rehabbedSqft > currentSqft;
    }

    // FIELDS
    case "general_contractor":
      return fieldIdToValue["borrower_strategy"] !== "Bridge";

    case "entity_mailing_address_line1":
    case "entity_mailing_address_line2":
    case "entity_mailing_city":
    case "entity_mailing_state":
    case "entity_mailing_zip":
      return fieldIdToValue["entity_mailing_address"] === "Yes";

    default:
      break;
  }

  if (
    baseId === 'entity_participant_mailing_address_line1' ||
    baseId === 'entity_participant_mailing_address_line2' ||
    baseId === 'entity_participant_mailing_city' ||
    baseId === 'entity_participant_mailing_state' ||
    baseId === 'entity_participant_mailing_zip'
  ) {
    const toggleFieldId = `entity_participant_use_different_mailing_address${suffix}`;
    return fieldIdToValue[toggleFieldId] === 'Yes';
  }

  return true;
}

export function hasVisibleSectionContent(
  section: ApplicationSection,
  fieldIdToValue: Record<string, string>
): boolean {
  if (section.isDynamicallyRenderedSection === true) return true;

  return (
    section.fields.some((field) => field.type !== 'heading' && isDisplayConditionTrue(field.id, fieldIdToValue))
    || (section.documents?.some((document) => isDisplayConditionTrue(document.id, fieldIdToValue)) ?? false)
  );
}

export function isOptionalConditionTrue(fieldOrDocId: string, fieldIdToValue: Record<string, string>): boolean {
  switch (fieldOrDocId) {
    case "building_permit_doc":
      return fieldIdToValue["loan_purpose"] === "Purchase";
    default:
      return false;
  }
}

// Loan-scoped section memory written by the application save path so reopened
// loans can restore sidebar state without relying on browser-local storage.
export const OPENED_OPTIONAL_SECTION_IDS_FIELD = 'openedOptionalSectionIds';

function isSectionVisibleForStage(sectionId: string, loanStage?: string): boolean {
  if (sectionId === GUARANTOR_INFORMATION_SECTION_ID) {
    const normalizedStage = normalizeLoanStage(loanStage);
    const stageOrder = [
      LoanStatus.DRAFT,
      LoanStatus.PRE_QUALIFIED,
      LoanStatus.PROCESSING,
      LoanStatus.UNDERWRITING,
      LoanStatus.CLOSING,
      LoanStatus.CLOSED,
    ];

    const stageIndex = stageOrder.indexOf(normalizedStage as LoanStatus);
    const processingIndex = stageOrder.indexOf(LoanStatus.PROCESSING);
    return stageIndex >= processingIndex;
  }

  return true;
}

function normalizeLoanStage(loanStage?: string): string {
  const value = (loanStage || '').trim();
  if (!value) return '';

  if (Object.values(LoanStatus).includes(value as LoanStatus)) {
    return value;
  }

  switch (value.toLowerCase()) {
    case 'draft':
      return LoanStatus.DRAFT;
    case 'pre-qualification':
      return LoanStatus.PRE_QUALIFIED;
    case 'processing':
      return LoanStatus.PROCESSING;
    case 'underwriting':
      return LoanStatus.UNDERWRITING;
    case 'closing':
      return LoanStatus.CLOSING;
    case 'closed':
      return LoanStatus.CLOSED;
    default:
      return value;
  }
}

// ---------------------------------------------------------------------------------------
// POPULATE SECTIONS WITH FIELDS & DOCUMENTS 
// Based on the current loan parameters, returns a filtered section-field list
// ---------------------------------------------------------------------------------------
export async function getApplicationSections(
  baseSections: (ApplicationSection | SectionGroup)[],
  loanPurpose?: string,
  exitStrategy?: string,
  propertyType?: string,
  loanStage?: string,
  projectSummaryAboveGroundSqft?: {current?: number; rehabbed?: number},
  fastTrack = false
): Promise<(ApplicationSection | SectionGroup)[]> {
  const filteredSections: (ApplicationSection | SectionGroup)[] = JSON.parse(
    JSON.stringify(baseSections)
  );
  if (exitStrategy === "Renovation / Fix & Flip") exitStrategy = "Renovation";

  await Promise.all(
    filteredSections.map(async (section) => {
      if (isSectionGroup(section)) {
        await Promise.all(
          section.sections.map(async (subSection) => {
            await filterSectionFields(subSection,
              loanPurpose, exitStrategy, propertyType, loanStage,
              projectSummaryAboveGroundSqft, fastTrack
            );
          })
        );

        section.sections = section.sections.filter(
          (s) => isSectionVisibleForStage(s.id, loanStage)
            && (s.isDynamicallyRenderedSection === true
            || (s.totalFields ?? 0) > 0
            || (s.documents?.length ?? 0) > 0)
        );
        section.totalFields = section.sections.reduce(
          (sum, s) => sum + (s.totalFields ?? 0), 0);
        section.completedFields = section.sections.reduce(
          (sum, s) => sum + (s.completedFields ?? 0), 0);
        return;
      }

      await filterSectionFields(section,
        loanPurpose, exitStrategy, propertyType, loanStage,
        projectSummaryAboveGroundSqft, fastTrack
      );
    })
  );

  // Filter: keep sections with fields.
  return filteredSections.filter((s) => {
    if (!isSectionVisibleForStage(s.id, loanStage)) return false;

    const hasContent = isSectionGroup(s)
      ? s.sections.length > 0
      : s.isDynamicallyRenderedSection === true
      || (s.totalFields ?? 0) > 0
      || (s.documents?.length ?? 0) > 0;
    return hasContent;
  });
}

async function filterSectionFields( section: ApplicationSection, loanPurpose?: string,
  exitStrategy?: string, propertyType?: string, loanStage?: string, 
  projectSummaryAboveGroundSqft?: {current?: number; rehabbed?: number},
  fastTrack = false
): Promise<void> {
  try {
    // 1. Read the relevant fields from application-sections-fields.csv (or its fast-track copy) based on loan parameters
    const filteredConfigs = await ApiCache.getApplicationSections(section.title,
      loanPurpose || "", exitStrategy || "", propertyType || "", loanStage || "", fastTrack);

    // 2. Create a map of field names to readonly status
    const nameToReadonly = new Map<string, boolean>();
    for (const cfg of filteredConfigs) {
      nameToReadonly.set(cfg.name, !!cfg.readonly);
    }

    // 3. Filter fields based on the returned configs (match by label or id) and set readonly
    const actionableFields = section.fields
      .filter((field) => field.type !== "heading")
      .filter((field) => nameToReadonly.has(field.id))
      .map((field) => {
        const ro = nameToReadonly.get(field.id);
        return { ...field, readonly: ro === true };
      });

    // 4. Update the section fields
    const headingFields = section.fields.filter((field) => field.type === "heading");
    section.fields = (headingFields.length > 0 || actionableFields.length > 0)
      ? [...headingFields, ...actionableFields]
      : [];

    // 5. Filter documents based on the same Y/N conditions from CSV
    if (section.documents) {
      section.documents = section.documents.filter(
        (doc) => nameToReadonly.has(doc.name) || nameToReadonly.has(doc.id)
      );
    }

    // 6. Clear section content if the section is not displayed
    if (section.id === "upload_active_building_permit" || section.id === "upload_building_plans") {
      const derivedFieldIdToValue: Record<string, string> = {
        loan_type: exitStrategy || "",
        project_above_current_sqft: projectSummaryAboveGroundSqft?.current === undefined ? "" : String(projectSummaryAboveGroundSqft.current),
        project_above_rehab_sqft:   projectSummaryAboveGroundSqft?.rehabbed === undefined ? "" : String(projectSummaryAboveGroundSqft.rehabbed),
      };

      if (!isDisplayConditionTrue(section.id, derivedFieldIdToValue)) {
        clearSectionContent(section);
        return;
      }
    }

    updateSectionFieldCounts(section);
  } catch (error) {
    CommonUtils.logError(`Error filtering fields for section ${section.id}:`, error);
    clearSectionContent(section);
  }
}

function clearSectionContent(section: ApplicationSection): void {
  section.fields = [];
  if (section.documents) section.documents = [];
  section.totalFields = 0;
  section.completedFields = 0;
}

function hasNonEmptyFieldValue(value: unknown): boolean {
  return value !== undefined && value !== null && value !== "";
}

function updateSectionFieldCounts(section: ApplicationSection): void {
  const countableFields = section.fields.filter((field) => field.type !== "heading");
  section.totalFields = countableFields.length;
  section.completedFields = countableFields.filter((field) => hasNonEmptyFieldValue(field.value)).length;
}

// ---------------------------------------------------------------------------------------
// CALCULATE SECTION STATUS
// ---------------------------------------------------------------------------------------
export function getSectionStatus(
  fields: ApplicationField[],
  documents: { documentId: string; status: string; required: boolean }[],
  getFieldAnnotations: (fieldId: string) => { resolved: boolean }[],
  getDocumentAnnotations: (documentId: string) => { resolved: boolean }[]
): "not_started" | "in_progress" | "completed" | "needs_attention" {
  const actionableFields = fields.filter((f) => f.type !== "heading");
  const hasMeaningfulValue = (value: unknown): boolean => {
    if (value === undefined || value === null) {
      return false;
    }
    if (typeof value === "string") {
      return value.trim() !== "";
    }
    return true;
  };

  // If section has no fields and no documents, it's not started
  if (actionableFields.length === 0 && documents.length === 0) {
    return "not_started";
  }

  for (const field of actionableFields) {
    const unresolved = getFieldAnnotations(field.id)?.some((a) => !a.resolved);
    if (unresolved) return "needs_attention";
  }

  for (const doc of documents) {
    const hasUnresolvedAnnotations = getDocumentAnnotations(doc.documentId)?.some((a) => !a.resolved);
    if (hasUnresolvedAnnotations) {
      return "needs_attention";
    }

    if (
      doc.required &&
      doc.status === DocumentStatus.NEEDS_REVIEW
    ) {
      return "needs_attention";
    }
  }

  const requiredFields = actionableFields.filter((f) => f.required);
  const requiredDocs = documents.filter((d) => d.required);

  // If there are no required fields and no required documents, check if any fields/documents exist
  if (requiredFields.length === 0 && requiredDocs.length === 0) {
    // If there are optional fields or documents, check if any have values
    const hasAnyFieldValue = actionableFields.some(
      (f) => hasMeaningfulValue(f.value)
    );
    const hasAnyDocProgress = documents.some(
      (d) => d.status !== DocumentStatus.NOT_STARTED
    );
    if (!hasAnyFieldValue && !hasAnyDocProgress) {
      return "not_started";
    }
    // If there are values/progress, it's completed
    return "completed";
  }

  // Check if all required fields are filled (if any exist)
  const allFieldsFilled = requiredFields.length === 0 || requiredFields
    .every((f) => hasMeaningfulValue(f.value));
  // Check if all required documents are approved (if any exist)
  const allDocsApproved = requiredDocs.length === 0 || requiredDocs
    .every((d) => d.status === DocumentStatus.COMPLETED);
  if (allFieldsFilled && allDocsApproved)
    return "completed";
  return "in_progress";
}

// ---------------------------------------------------------------------------------------
// Copy helpers (LOAN <-> FORM)
// ---------------------------------------------------------------------------------------
const PROPERTY_INFO_MAP = {
  fields: {
    property_contact_first_name: 'firstName',
    property_contact_last_name: 'lastName',
    property_contact_phone: 'phone',
    property_contact_email: 'email',
  },
  documents: {
    purchase_contract_doc: "purchaseContractDoc",
    contract_assignment_doc: "contractAssignmentDoc",
    mortgage_statement_doc: "mortgageStatementDoc",
    property_comparables_doc: "propertyComparablesDoc",
    rent_roll_opex_doc: "rentRollOpexDoc",
    in_place_leases_doc: "inPlaceLeasesDoc",
  },
} as const;

const PROJECT_INFO_MAP = {
  fields: {
    project_manager: "projectManager",
    additional_comments: "additionalComments",
    general_contractor_first_name: "generalContractorFirstName",
    general_contractor_last_name: "generalContractorLastName",
    general_contractor_phone: "generalContractorPhone",
    license_state_or_city: "licenseStateOrCity",
    license_number: "licenseNumber",
  },
  documents: {
    building_plans_doc: "buildingPlans",
    building_permit_doc: "buildingPermit",
  },
};

const ENTITY_INFO_MAP = {
  fields: {
    entity_name: "entityName",
    entity_email: "entityEmail",
    entity_type: "entityType",
    entity_state_of_formation: "entityStateOfFormation",
    entity_ein_number: "entityEinNumber",
    entity_present_address_line1: "entityPresentAddressLine1",
    entity_present_address_line2: "entityPresentAddressLine2",
    entity_present_city: "entityPresentCity",
    entity_present_state: "entityPresentState",
    entity_present_zip: "entityPresentZip",
    entity_mailing_address: "entityMailingAddress",
    entity_mailing_address_line1: "entityMailingAddressLine1",
    entity_mailing_address_line2: "entityMailingAddressLine2",
    entity_mailing_city: "entityMailingCity",
    entity_mailing_state: "entityMailingState",
    entity_mailing_zip: "entityMailingZip",
  },
  documents: {}
};

const BORROWER_INFO_MAP = {
  documents: {
    entity_formation_doc: 'entityFormationDoc',
    irs_ein_letter_doc: 'irsEinLetterDoc',
    operating_agreement_doc: 'operatingAgreementDoc',
    certificate_of_good_standing_doc: 'certificateOfGoodStandingDoc',
    most_recent_bank_statement_doc: 'mostRecentBankStatementDoc',
    previous_bank_statement_doc: 'previousBankStatementDoc',
  },
};

function copyEntityParticipantInfoFromLoanDataToField(loanData: LoanAppData, fieldId: string): string | undefined {
  const storedValue = getBorrowerInfoEntryValue(loanData, ENTITY_PARTICIPANT_STORAGE_ID, fieldId);
  if (storedValue !== undefined) return storedValue;

  const rawLoanData = loanData.getRawLoanData() as any;
  const contactInfo = rawLoanData?.contactInfo as Record<string, unknown> | undefined;

  // Main Guarantor : Fields that need to be pre-filled from contactInfo
  switch (fieldId) {
    case 'entity_participant_first_name': {
      const fallback = contactInfo?.firstName;
      return fallback === undefined || fallback === null ? '' : String(fallback);
    }
    case 'entity_participant_last_name': {
      const fallback = contactInfo?.lastName;
      return fallback === undefined || fallback === null ? '' : String(fallback);
    }
    case 'entity_participant_email': {
      const fallback = contactInfo?.email;
      return fallback === undefined || fallback === null ? '' : String(fallback);
    }
    case 'entity_participant_phone': {
      const fallback = contactInfo?.phone;
      return fallback === undefined || fallback === null ? '' : String(fallback);
    }
    case 'entity_participant_address_line1': {
      const fallback = contactInfo?.addressLine1;
      return fallback === undefined || fallback === null ? '' : String(fallback);
    }
    case 'entity_participant_address_line2': {
      const fallback = contactInfo?.addressLine2;
      return fallback === undefined || fallback === null ? '' : String(fallback);
    }
    case 'entity_participant_city': {
      const fallback = contactInfo?.city;
      return fallback === undefined || fallback === null ? '' : String(fallback);
    }
    case 'entity_participant_state': {
      const fallback = contactInfo?.state;
      return fallback === undefined || fallback === null ? '' : String(fallback);
    }
    case 'entity_participant_zip': {
      const fallback = contactInfo?.zipCode;
      return fallback === undefined || fallback === null ? '' : String(fallback);
    }
    case 'entity_participant_date_of_birth': {
      const fallback = contactInfo?.dateOfBirth;
      return fallback === undefined || fallback === null ? '' : String(fallback);
    }
    default:
      return undefined;
  }
}

function copyEntityParticipantInfoFromFieldToLoanData(
  loanAppData: LoanAppData,
  fieldId: string,
  value: string
): boolean {
  const loanData = loanAppData.getRawLoanData();

  // 5.3 BORROWER INFORMATION - ENTITY PARTICIPANTS
  if (fieldId === ENTITY_PARTICIPANT_STORAGE_ID) {
    const entries = parseJsonArray<any>(value)
      .map((entry) => pruneParticipantEntry(entry))
      .filter((entry): entry is Record<string, unknown> => Boolean(entry));
    const borrowerInformation = ((loanData as any).borrowerInformation ??= {});

    Object.keys(borrowerInformation)
      .filter((key) => key.startsWith('entity_participant_') && key !== ENTITY_PARTICIPANT_STORAGE_ID)
      .forEach((key) => {
        delete borrowerInformation[key];
      });

    if (entries.length === 0) {
      delete borrowerInformation[ENTITY_PARTICIPANT_STORAGE_ID];
      if (Object.keys(borrowerInformation).length === 0) {
        delete (loanData as any).borrowerInformation;
      }
    } else {
      borrowerInformation[ENTITY_PARTICIPANT_STORAGE_ID] = entries;
    }
    return true;
  }

  // 5.2 BORROWER INFORMATION - MAIN GUARANTOR INFORMATION
  switch (fieldId) {
    case 'entity_participant_first_name':
      loanData.contactInfo = {
        ...(loanData.contactInfo ?? {}),
        firstName: value || '',
      };
      return true;
    case 'entity_participant_last_name':
      loanData.contactInfo = {
        ...(loanData.contactInfo ?? {}),
        lastName: value || '',
      };
      return true;
    case 'entity_participant_email':
      loanData.contactInfo = {
        ...(loanData.contactInfo ?? {}),
        email: value || '',
      };
      return true;
    case 'entity_participant_phone':
      loanData.contactInfo = {
        ...(loanData.contactInfo ?? {}),
        phone: value || '',
      };
      return true;
    case 'entity_participant_address_line1':
      loanData.contactInfo = {
        ...(loanData.contactInfo ?? {}),
        addressLine1: value || '',
      };
      return true;
    case 'entity_participant_address_line2':
      loanData.contactInfo = {
        ...(loanData.contactInfo ?? {}),
        addressLine2: value || '',
      };
      return true;
    case 'entity_participant_city':
      loanData.contactInfo = {
        ...(loanData.contactInfo ?? {}),
        city: value || '',
      };
      return true;
    case 'entity_participant_state':
      loanData.contactInfo = {
        ...(loanData.contactInfo ?? {}),
        state: value || '',
      };
      return true;
    case 'entity_participant_zip':
      loanData.contactInfo = {
        ...(loanData.contactInfo ?? {}),
        zipCode: value || '',
      };
      return true;
    case 'entity_participant_date_of_birth':
      loanData.contactInfo = {
        ...(loanData.contactInfo ?? {}),
        dateOfBirth: value || '',
      };
      return true;
    default:
      return false;
  }
}

export function copyLoanDataToField(loanData: LoanAppData,fieldId: string): string {
  let result: string | undefined;

  // 1.4 GENERAL INFORMATION - REAL ESTATE INVESTMENT EXPERIENCE
  if (fieldId.startsWith("real_estate_experience_")) {
    const rawLoanData = loanData.getRawLoanData() as any;
    const entries = rawLoanData?.realEstateExperience;
    if (Array.isArray(entries) && entries.length > 0) {
      const indexMatch = fieldId.match(/__(\d+)$/);
      const entryIndex = indexMatch ? Math.max(Number(indexMatch[1]) - 1, 0) : 0;
      const baseFieldId = indexMatch ? fieldId.replace(/__(\d+)$/, "") : fieldId;
      const entry = entries[entryIndex] ?? entries[0];

      if (entry && typeof entry === "object" && !Array.isArray(entry)) {
        const value = (entry as any)[baseFieldId];
        if (value !== undefined && value !== null) {
          return typeof value === "string" || typeof value === "number"
            ? String(value)
            : "";
        }
      }
    }
    return "";
  }

  // 2.1 PROPERTY INFORMATION - PROPERTY CONTACT INFORMATION
  if (fieldId.startsWith('property_contact_')) {
    const rawLoanData = loanData.getRawLoanData() as any;
    const container = (rawLoanData?.propertyDetails?.propertyContact as Record<string, any>) || {};
    return getMappedContainerValueAsString(container, PROPERTY_INFO_MAP.fields, fieldId);
  }

  // 3.1 PROJECT INFORMATION - PROJECT MANAGEMENT
  if (fieldId.startsWith("general_contractor") || Object.prototype.hasOwnProperty.call(PROJECT_INFO_MAP.fields, fieldId)) {
    const rawLoanData = loanData.getRawLoanData() as any;
    const projectManagement = (rawLoanData?.projectManagement as Record<string, any>) || {};
    return getMappedContainerValueAsString(projectManagement, PROJECT_INFO_MAP.fields, fieldId);
  }

  // 3.2 PROJECT INFORMATION - PROJECT SUMMARY
  if (fieldId.startsWith("project_")) {
    if (fieldId === "project_above_current_property_type") {
      return loanData.getPropertyDetails?.()?.propertyType?.display || "";
    }
    const rawLoanData = loanData.getRawLoanData() as any;
    const projectSummary = (rawLoanData?.projectSummary as Record<string, unknown>) || {};
    const value = projectSummary[fieldId];
    if (value === undefined || value === null) return "";
    return typeof value === "string" || typeof value === "number" ? String(value) : "";
  }

  // 4.1 THIRD-PARTY INFORMATION
  const thirdPartyValue = readThirdPartyReportField(loanData.getRawLoanData() as any, fieldId);
  if (thirdPartyValue !== undefined) return thirdPartyValue;

  // 5.1 BORROWER INFORMATION - ENTITY INFORMATION
  if (Object.prototype.hasOwnProperty.call(ENTITY_INFO_MAP.fields, fieldId)) {
    const entityInfo = (loanData.getRawLoanData() as any).entityInfo as Record<string, unknown> | undefined;
    if (!entityInfo) return "";
    const key = ENTITY_INFO_MAP.fields[fieldId as keyof typeof ENTITY_INFO_MAP.fields];
    const value = entityInfo[key];
    if (value === undefined || value === null) return "";
    return typeof value === "string" || typeof value === "number" ? String(value) : "";
  }

  // 5.2 BORROWER INFORMATION - ENTITY PARTICIPANT INFORMATION
  const entityParticipantValue = copyEntityParticipantInfoFromLoanDataToField(loanData, fieldId);
  if (entityParticipantValue !== undefined) return entityParticipantValue;

  switch (fieldId) {

    // 1.1 GENERAL INFORMAION - LOAN DETAILS
    case "loan_purpose": result = loanData.getLoanDetails().loanPurpose?.display; break;
    case "loan_type": result = loanData.getPurchaseDetails().borrowerStrategy; break;
    case "purchase_price": result = loanData.getPurchaseDetails()?.purchasePrice; break;
    case "original_purchase_date": 
      result = loanData.getPurchaseDetails()?.originalPurchaseDate; break;
    case "rehab_costs_spent":
      result = loanData.getPurchaseDetails().rehabCostsSpent; break;
    case "as_is_value": result = loanData.getLoanDetails().asIsValue; break;
    case "existing_debt": result = loanData.getPurchaseDetails().existingDebt; break;
    case "refinance_loan_type": result = (loanData.getRawLoanData() as any)?.loanInfoDetails?.refinance_loan_type; break;
    case "existing_loan_good_standing":
      result = (loanData.getRawLoanData() as any)?.loanInfoDetails?.existing_loan_good_standing; break;
    case "rehab_budget": result = loanData.getLoanDetails().rehabCost; break;
    case "arv": result = loanData.getLoanDetails().afterRepairValue; break;
    case "seller_concessions": result = (loanData.getRawLoanData() as any)?.loanInfoDetails?.seller_concessions; break;
    case "assignment_fee":  result = loanData.getPurchaseDetails().assignmentFee; break;
    case "preferred_signing_date": result = (loanData.getRawLoanData() as any)?.loanDetails?.additionalDetails?.signingDate; break;
    case "minimum_available_liquidity": {
      const rawLoanData = loanData.getRawLoanData() as any;
      const minimumAvailableLiquidity =
        rawLoanData?.loanDetails?.minimumAvailableLiquidity
        ?? rawLoanData?.result?.options?.[0]?.loanDetails?.minimumAvailableLiquidity;
      result = minimumAvailableLiquidity === undefined || minimumAvailableLiquidity === null
        ? ''
        : String(minimumAvailableLiquidity);
      break;
    }

    // 1.2 GENERAL INFORMAION - PROPERTY DETAILS
    case "property_address": result = loanData.getPropertyDetails().propertyAddress; break;
    case "property_state": result = loanData.getPropertyDetails().propertyState.display; break;
    case "property_type": result = loanData.getPropertyDetails().propertyType?.display; break;
    case "residential_unit_count": result = loanData.getPropertyDetails().residentialUnitCount; break;
    case "commercial_unit_count": result = loanData.getPropertyDetails().commercialUnitCount; break;
    case "tenant_occupied": result = (loanData.getRawLoanData() as any)?.propertyDetails?.tenant_occupied; break;
    case "accepted_purchase_contract": 
      result = (loanData.getRawLoanData() as any)?.propertyDetails?.accepted_purchase_contract;
      break;

    // 1.3 GENERAL INFORMAION - EXIT STRATEGY DETAILS
    case "days_to_finish_rehab_project":
      result = loanData.getRawLoanData().exitStrategyDetails?.daysToFinishRehabProject;
      break;
    case "detailed_exit_strategy":
      result = loanData.getRawLoanData().exitStrategyDetails?.detailedExitStrategy;
      break;
    
    case LIQUIDITY_ACCOUNT_TYPE_FIELD_ID: {
      const rawLoanData = loanData.getRawLoanData() as any;
      const liquidityAccounts = getLiquidityAccountEntries(rawLoanData);
      if (liquidityAccounts.length > 0) {
        result = liquidityAccounts[0]?.[LIQUIDITY_ACCOUNT_TYPE_FIELD_ID] || '';
        break;
      }
    }

    default: {
      // MISCELLANEOUS
      if (fieldId.startsWith("custom_")) {
        const rawLoanData = loanData.getRawLoanData() as any;
        const miscFields = rawLoanData?.miscellaneousFields || {};
        const normalizedFieldId = normalizeCustomFieldId(fieldId);
        const storedValue =
          miscFields[normalizedFieldId] ??
          miscFields[normalizedFieldId.replace("custom_", "")];
        result =
          typeof storedValue === "string" || typeof storedValue === "number"
            ? String(storedValue)
            : "";
        break;
      }

      // if (isBorrowerInfoFieldId(fieldId)) {
      //   const rawLoanData = loanData.getRawLoanData() as any;
      //   const storedValue = rawLoanData?.borrowerInformation?.[fieldId];
      //   result =
      //     storedValue === undefined || storedValue === null
      //       ? ""
      //       : String(storedValue);
      //   break;
      // }

      // Try address field utility first
      result = copyAddressDataToField(loanData, fieldId);
      if (!result) result = "";
      break;
    }
  }

  return result || "";
}

export function copyLoanDataToDocument(loanAppData: LoanAppData, docId: string): DocumentUploaded | undefined {
  let document: DocumentUploaded | undefined = undefined;
  const loanData = loanAppData.getRawLoanData();
  const { baseDocId, hasSuffix, suffixNumber } = parseDocumentIdSuffix(docId);

  // 2.2 PROPERTY INFORMATION - UPLOAD DOCUMENTS
  if (Object.prototype.hasOwnProperty.call(PROPERTY_INFO_MAP.documents, baseDocId)) {
    const key = PROPERTY_INFO_MAP.documents[baseDocId as keyof typeof PROPERTY_INFO_MAP.documents];
    const docs = Array.isArray((loanData as any)?.propertyDetails?.[key])
      ? ((loanData as any).propertyDetails[key] as DocumentUploaded[])
      : [];
    if (isLiquidityStatementDocId(baseDocId)) {
      if (hasSuffix) return docs[suffixNumber - 1];
      return docs[0];
    }
    return pickLast(docs);
  }

  // 3.2 PROJECT INFORMATION - UPLOAD DOCUMENTS
  if (Object.prototype.hasOwnProperty.call(PROJECT_INFO_MAP.documents, baseDocId)) {
    const key = PROJECT_INFO_MAP.documents[baseDocId as keyof typeof PROJECT_INFO_MAP.documents];
    return pickLast((loanData as any)?.projectManagement?.[key]);
  }

  // 5.1 BORROWER INFORMATION - ENTITY INFO - UPLOAD DOCUMENTS
  if (Object.prototype.hasOwnProperty.call(BORROWER_INFO_MAP.documents, baseDocId)) {
    const key = BORROWER_INFO_MAP.documents[baseDocId as keyof typeof BORROWER_INFO_MAP.documents];
    const docs = Array.isArray((loanData as any)?.borrowerInformation?.[key])
      ? ((loanData as any).borrowerInformation[key] as DocumentUploaded[])
      : [];
    if (hasSuffix) return docs[suffixNumber - 1];
    return docs[0];
  }

  if (Object.prototype.hasOwnProperty.call(ENTITY_INFO_MAP.documents, baseDocId)) {
    const key = ENTITY_INFO_MAP.documents[baseDocId as keyof typeof ENTITY_INFO_MAP.documents];
    const docs = Array.isArray((loanData as any)?.entityInfo?.[key])
      ? ((loanData as any).entityInfo[key] as DocumentUploaded[]) : [];
    if (isLiquidityStatementDocId(baseDocId)) {
      if (hasSuffix) return docs[suffixNumber - 1];
      return docs[0];
    }
    return pickLast(docs);
  }

  // 5.2 BORROWER INFORMATION - ENTITY PARTICIPANT INFO - UPLOAD DOCUMENTS
  if (baseDocId === 'government_issued_id_doc') {
    const mappedPath = DOCUMENT_ARRAY_PATHS[baseDocId];
    const docs = mappedPath && Array.isArray((loanData as any)?.[mappedPath.sectionKey]?.[mappedPath.arrayKey])
      ? (((loanData as any)[mappedPath.sectionKey] as any)[mappedPath.arrayKey] as DocumentUploaded[])
      : [];
    return pickGovtIssuedIdDoc<DocumentUploaded>(baseDocId, docs, { hasSuffix, suffixNumber });
  }

  // TODO-Sree-P1: Is this needed?
  switch (baseDocId) {
    default:
      if (isBorrowerInfoDocId(baseDocId)) {
        const docs = Array.isArray((loanData as any)?.borrowerDocuments?.[baseDocId])
          ? (loanData as any).borrowerDocuments[baseDocId]
          : [];
        document = pickLast(docs);
      }
      break;
  }
  return document;
}

export interface CopyLoanDataOptions {
  persistFields?: boolean;
  persistDocuments?: boolean;
  persistSow?: boolean;
}

export function copyFormDataToLoanData(
  loanData: LoanAppData,
  fieldIdToValue: Record<string, string>,
  docIdToUploadedDoc: Record<string, DocumentUploaded | undefined>,
  sowData?: SOWData,
  options?: CopyLoanDataOptions
): LoanAppData {
  const updatedLoanData = loanData; // mutate intentionally; caller owns instance
  const {
    persistFields = true,
    persistDocuments = true,
    persistSow = true,
  } = options ?? {};

  if (persistFields) {
    Object.entries(fieldIdToValue).forEach(([fieldId, value]) => {
      copyFieldToLoanData(updatedLoanData, fieldId, value ?? "");
    });
  }

  if (persistDocuments) {
    Object.entries(docIdToUploadedDoc).forEach(([docId, document]) => {
      if (!document) return;
      if (docId.startsWith("custom_")) {
        const rawLoanData = updatedLoanData.getRawLoanData() as any;
        rawLoanData.miscellaneousFields = rawLoanData.miscellaneousFields || {};
        const normalizedDocId = docId.startsWith("custom_custom_")
          ? docId.replace("custom_custom_", "custom_")
          : docId;
        rawLoanData.miscellaneousFields[normalizedDocId] = document;
        return;
      }

      copyDocumentToLoanData(updatedLoanData, document);
    });
  }

  if (persistSow && sowData) {
    copySOWToLoanData(updatedLoanData, sowData);
  }

  return updatedLoanData;
}

export function copyFieldToLoanData(loanAppData: LoanAppData, fieldId: string, value: string): void {
  const loanData = loanAppData.getRawLoanData();
  
  // 2.1 PROPERTY INFORMATION - PROPERTY CONTACT
  if (fieldId.startsWith('property_contact_')) {
    const mappedFieldId = PROPERTY_INFO_MAP.fields[fieldId as keyof typeof PROPERTY_INFO_MAP.fields];
    if (!mappedFieldId) return;
    (loanData.propertyDetails ??= {} as any).propertyContact = {
      ...((loanData.propertyDetails as any).propertyContact ?? {}),
      [mappedFieldId]: value || "",
    };
    return;
  }

  // 3.1 PROJECT INFORMATION - PROJECT MANAGEMENT
  if (fieldId.startsWith("general_contractor") || Object.prototype.hasOwnProperty.call(PROJECT_INFO_MAP.fields, fieldId)) {
    const mappedFieldId = PROJECT_INFO_MAP.fields[fieldId as keyof typeof PROJECT_INFO_MAP.fields] || fieldId;
    ((loanData.projectManagement ??= {}) as Record<string, any>)[mappedFieldId] = value || "";
    return;
  }

  // 3.2 PROJECT INFORMATION - PROJECT SUMMARY
  if (fieldId.startsWith("project_")) {
    (loanData as any).projectSummary = {
      ...((loanData as any).projectSummary ?? {}),
      [fieldId]: value || "",
    };
    return;
  }

  // 4.1 THIRD PARTY INFORMATION
  if (writeThirdPartyReportField(loanData as any, fieldId, value)) return;

  // 5.1 BORROWER INFORMATION - ENTITY INFORMATION
  if (Object.prototype.hasOwnProperty.call(ENTITY_INFO_MAP.fields, fieldId)) {
    const key = ENTITY_INFO_MAP.fields[fieldId as keyof typeof ENTITY_INFO_MAP.fields];
    setEntityInfoField(loanData, key, value);
    return;
  }
  // 5.2 BORROWER INFORMATION - ENTITY PARTICIPANT INFORMATION
  if (copyEntityParticipantInfoFromFieldToLoanData(loanAppData, fieldId, value)) return;

  // 5.4 BORROWER INFORMATION - LIQUIDITY
  if (fieldId === LIQUIDITY_STORAGE_FIELD_ID) {
    copyLiquidityAccountsToBorrowerInformation(loanData as any, value);
    return;
  }
  
  switch (fieldId) {
    // 1.1 GENERAL INFORMAION - LOAN DETAILS
    case "loan_purpose":
      loanData.loanPurpose = FormDataUtils.stringToLoanPurpose(value || ""); break;
    case "loan_type":
      loanData.exitStrategy = FormDataUtils.stringToExitStrategy(value || "");
      break;
    case "purchase_price":
      setRawLoanDataStringField(loanData, "loanInfo", "purchasePrice", value);
      (loanData.purchaseDetails ??= {}).purchasePrice = value || ""; break;
    case "original_purchase_date":
      setRawLoanDataStringField(loanData, "loanInfo", "originalPurchaseDate", value);
      (loanData.purchaseDetails ??= {}).originalPurchaseDate = value || "";
      break;
    case "rehab_costs_spent":
      setRawLoanDataStringField(loanData, "loanInfo", "rehabCompletedToDate", value);
      (loanData.purchaseDetails ??= {}).rehabCostsSpent = value || "";
      break;
    case "as_is_value":
      setRawLoanDataStringField(loanData, "loanInfo", "asIsValue", value);
      break;
    case "existing_debt":
      setRawLoanDataStringField(loanData, "loanInfo", "existingDebt", value);
      break;
    case "refinance_loan_type":
      (loanData as any).loanInfoDetails = {
        ...((loanData as any).loanInfoDetails ?? {}),
        refinance_loan_type: value || "",
      };
      break;
    case "existing_loan_good_standing":
      (loanData as any).loanInfoDetails = {
        ...((loanData as any).loanInfoDetails ?? {}),
        existing_loan_good_standing: value || "",
      };
      break;
    case "rehab_budget":
      setRawLoanDataStringField(loanData, "loanInfo", "rehabCost", value);
      break;
    case "arv":
      setRawLoanDataStringField(loanData, "loanInfo", "afterRepairValue", value);
      break;
    case "seller_concessions":
      (loanData as any).loanInfoDetails = {
        ...((loanData as any).loanInfoDetails ?? {}),
        seller_concessions: value || "",
      };
      break;
    case "assignment_fee":
      (loanData.purchaseDetails ??= {}).assignmentFee = value ?? "";
      break;
    case "preferred_signing_date":
      (((loanData as any).loanDetails ??= {}).additionalDetails ??= {}).signingDate = value || "";
      break;
    case "minimum_available_liquidity":
      // Read-only reference from pricing/prequal output; do not persist from application form input.
      break;

    // 1.2 GENERAL INFORMAION - PROPERTY DETAILS
    case "property_type":
      loanData.propertyType = FormDataUtils.stringToPropertyType(value || "");
      break;
    // TODO-SREE-P1: address is not saved to loan
    // property_state write-back depends on canonical schema; skipping explicit write to avoid schema mismatch
    case "residential_unit_count":
      if (loanData.propertyDetails)
        loanData.propertyDetails.residentialUnitCount = parseInt(value || "0");
      break;
    case "commercial_unit_count":
      if (loanData.propertyDetails)
        loanData.propertyDetails.commercialUnitCount = parseInt(value || "0");
      break;
    case "tenant_occupied":
      (loanData.propertyDetails ??= {} as any).tenant_occupied = value || "";
      break;
    case "accepted_purchase_contract":
      (loanData.propertyDetails ??= {} as any).accepted_purchase_contract = value || "";
      break;

    // 1.3 GENERAL INFORMATION - EXIT STRATEGY DETAILS
    case "days_to_finish_rehab_project":
      setRawLoanDataStringField(loanData, "exitStrategyDetails", "daysToFinishRehabProject", value);
      break;
    case "detailed_exit_strategy":
      setRawLoanDataStringField(loanData, "exitStrategyDetails", "detailedExitStrategy", value);
      break;

    // 1.4 GENERAL INFORMATION - REAL ESTATE INVESTMENT EXPERIENCE
    case 'real_estate_experience_items': {
      const parsedEntries = parseJsonArray<any>(value);
      (loanData as any).realEstateExperience = parsedEntries;
      break;
    }

    // MISCELLANEOUS Custom fields - stored in miscellaneousFields
    default:
      // Check if this is a custom field (starts with 'custom_')
      if (fieldId.startsWith("custom_")) {
        const normalizedFieldId = fieldId.startsWith("custom_custom_")
          ? fieldId.replace("custom_custom_", "custom_")
          : fieldId;
        // Store in miscellaneousFields
        if (!loanData.miscellaneousFields) {
          loanData.miscellaneousFields = {};
        }
        loanData.miscellaneousFields[normalizedFieldId] = value || "";
      } else if (isBorrowerInfoFieldId(fieldId)) {
        if (fieldId.startsWith('entity_participant_')) {
          break;
        }
        (loanData.borrowerInformation ??= {})[fieldId] = value || "";
      } else {
        // Try address field utility for non-custom fields
        copyAddressDataFromField(loanAppData, fieldId, value);
      }
      break;
  }
}

export function copyDocumentToLoanData(
  loanAppData: LoanAppData,
  document: DocumentUploaded
) {
  const loanData = loanAppData.getRawLoanData();
  const { baseDocId, hasSuffix, suffixNumber } = parseDocumentIdSuffix(document.id);

  // 2.2 PROPERTY INFORMATION - UPLOAD DOCUMENTS
  if (Object.prototype.hasOwnProperty.call(PROPERTY_INFO_MAP.documents, baseDocId)) {
    const key = PROPERTY_INFO_MAP.documents[baseDocId as keyof typeof PROPERTY_INFO_MAP.documents];
    safePush(loanData, "propertyDetails", key, document);
    return;
  }

  // 3.2 PROJECT INFORMATION - UPLOAD DOCUMENTS
  if (Object.prototype.hasOwnProperty.call(PROJECT_INFO_MAP.documents, baseDocId)) {
    const key = PROJECT_INFO_MAP.documents[baseDocId as keyof typeof PROJECT_INFO_MAP.documents];
    safePush(loanData, "projectManagement", key, document);
    return;
  }

  // 5.1 BORROWER INFORMATION - ENTITY INFO -UPLOAD DOCUMENTS
  if (Object.prototype.hasOwnProperty.call(BORROWER_INFO_MAP.documents, baseDocId)) {
    const key = BORROWER_INFO_MAP.documents[baseDocId as keyof typeof BORROWER_INFO_MAP.documents];
    const borrowerInformation = ((loanData as any).borrowerInformation ??= {}) as Record<string, unknown>;
    const existingDocs = Array.isArray(borrowerInformation[key])
      ? (borrowerInformation[key] as DocumentUploaded[])
      : [];
    const nextDocs = [...existingDocs];
    const targetIndex = hasSuffix ? (suffixNumber - 1) : 0;
    nextDocs[targetIndex] = document;
    borrowerInformation[key] = nextDocs;
    return;
  }

  if (Object.prototype.hasOwnProperty.call(ENTITY_INFO_MAP.documents, baseDocId)) {
    const key = ENTITY_INFO_MAP.documents[baseDocId as keyof typeof ENTITY_INFO_MAP.documents];
    if (isLiquidityStatementDocId(baseDocId)) {
      const entityInfo = ((loanData as any).entityInfo ??= {}) as Record<string, unknown>;
      const existingDocs = Array.isArray(entityInfo[key]) ? (entityInfo[key] as DocumentUploaded[]) : [];
      const nextDocs = [...existingDocs];
      const targetIndex = hasSuffix ? (suffixNumber - 1) : 0;
      nextDocs[targetIndex] = document;
      entityInfo[key] = nextDocs;
      return;
    }
    safePush(loanData, "entityInfo", key, document);
    return;
  }

  // 5.2 BORROWER INFORMATION - ENTITY PARTICIPANT INFO -UPLOAD DOCUMENTS
  if (baseDocId === 'government_issued_id_doc') {
    const mappedPath = DOCUMENT_ARRAY_PATHS[baseDocId];
    const section = (((loanData as any)[mappedPath.sectionKey] ||= {}) as Record<string, unknown>);
    storeGovtIssuedIdDoc<DocumentUploaded>(baseDocId, section, mappedPath.arrayKey, 
      document, { hasSuffix, suffixNumber });
    return;
  }

  // TODO-Sree-P1: Is this used?
  switch (baseDocId) {
    default:
      if (isBorrowerInfoDocId(baseDocId)) {
        safePushBorrowerDocument(loanData, baseDocId, document);
      }
      break;
  }
}

function parseDocumentIdSuffix(docId: string): { baseDocId: string; hasSuffix: boolean; suffixNumber: number; } {
  const suffixIndex = docId.indexOf('__');
  const baseDocId = suffixIndex === -1 ? docId : docId.slice(0, suffixIndex);
  const suffixValue = suffixIndex === -1 ? '' : docId.slice(suffixIndex + 2);
  const suffixNumber = suffixValue ? Number(suffixValue) : NaN;
  const hasSuffix = Number.isFinite(suffixNumber) && suffixNumber > 0;

  return { baseDocId, hasSuffix, suffixNumber };
}

const hasMeaningfulValue = (value: unknown): boolean => {
  if (value === undefined || value === null) return false;
  if (typeof value === 'string') return value.trim() !== '';
  if (typeof value === 'number') return Number.isFinite(value);
  if (Array.isArray(value)) return value.length > 0;
  return true;
};

function getMappedContainerValueAsString<T extends Record<string, string>>(
  container: Record<string, unknown> | undefined,
  mapping: T,
  fieldId: string
): string {
  const key = Object.prototype.hasOwnProperty.call(mapping, fieldId)
    ? mapping[fieldId as keyof T]
    : undefined;
  if (!key) return "";

  const value = (container ?? {})[key];
  if (value === undefined || value === null) return "";
  if (typeof value === "string" || typeof value === "number") return String(value);
  return "";
}

// -------------------------------------------
// Borrower Info and Entity Info Fields
// -------------------------------------------
const getBorrowerInfoEntryValue = (loanData: LoanAppData, storageKey: string, fieldId: string): string | undefined => {
  const rawLoanData = loanData.getRawLoanData() as any;
  const stored = rawLoanData?.borrowerInformation?.[storageKey];
  const entries = parseJsonArray<any>(stored);
  if (!entries.length) return undefined;
  const firstEntry = entries[0];
  if (!firstEntry || typeof firstEntry !== "object") return undefined;
  const value = firstEntry[fieldId];
  if (value === undefined || value === null) return undefined;
  return typeof value === "string" || typeof value === "number"
    ? String(value)
    : "";
};

const BORROWER_INFO_FIELD_PREFIXES = [
  "entity_participant_",
];
const ENTITY_PARTICIPANT_STORAGE_ID = "entity_participant_items";
const BORROWER_INFO_DOC_IDS = new Set([
  "entity_participant_bank_statements_doc",
  "entity_participant_articles_of_organization_doc",
  "entity_participant_certificate_of_good_standing_doc",
  "entity_participant_ein_ss4_letter_doc",
  "entity_participant_operating_agreement_doc",
  "entity_participant_valid_ids_doc",
]);

const isBorrowerInfoFieldId = (fieldId: string): boolean =>
  BORROWER_INFO_FIELD_PREFIXES.some((prefix) => fieldId.startsWith(prefix));

const isBorrowerInfoDocId = (docId: string): boolean =>
  BORROWER_INFO_DOC_IDS.has(docId);

const isLiquidityStatementDocId = (docId: string): boolean => (
  docId === LIQUIDITY_MOST_RECENT_DOC_ID || docId === LIQUIDITY_PREVIOUS_DOC_ID
);

const hasAnyMeaningfulAccountValue = (accounts: unknown): boolean => {
  if (!Array.isArray(accounts)) return false;

  return accounts.some((account) => {
    if (typeof account === 'string' || typeof account === 'number') {
      return hasMeaningfulValue(account);
    }
    if (!account || typeof account !== 'object' || Array.isArray(account)) {
      return false;
    }
    return Object.values(account as Record<string, unknown>).some((value) => hasMeaningfulValue(value));
  });
};

const pruneParticipantEntry = (entry: unknown): Record<string, unknown> | null => {
  if (!entry || typeof entry !== 'object' || Array.isArray(entry)) return null;
  const record = entry as Record<string, unknown>;

  const pruned: Record<string, unknown> = {};
  Object.entries(record).forEach(([key, value]) => {
    if (key === 'accounts') {
      if (!Array.isArray(value)) return;

      const prunedAccounts = value
        .filter((account) => account && typeof account === 'object' && !Array.isArray(account))
        .map((account) => {
          const accountRecord = account as Record<string, unknown>;
          const nextAccount: Record<string, unknown> = {};
          Object.entries(accountRecord).forEach(([accountKey, accountValue]) => {
            if (!hasMeaningfulValue(accountValue)) return;
            nextAccount[accountKey] = accountValue;
          });
          return nextAccount;
        })
        .filter((account) => Object.keys(account).length > 0);

      if (prunedAccounts.length > 0) {
        pruned.accounts = prunedAccounts;
      }
      return;
    }

    if (!hasMeaningfulValue(value)) return;
    pruned[key] = value;
  });

  if (!hasAnyMeaningfulParticipantValue(pruned)) return null;
  return pruned;
};

const hasAnyMeaningfulParticipantValue = (entry: unknown): boolean => {
  if (!entry || typeof entry !== 'object' || Array.isArray(entry)) return false;
  const record = entry as Record<string, unknown>;

  return Object.entries(record).some(([key, value]) => {
    if (key === 'accounts') {
      return hasAnyMeaningfulAccountValue(value);
    }
    return hasMeaningfulValue(value);
  });
};

// ---------------------------------------------------------------------------------------
// Address Field Utilities
// ---------------------------------------------------------------------------------------
type AddressFieldConfig = {
  fieldId: string;
  dataPath: string;
};

type AddressMapping = {
  readPath: (loanData: LoanAppData) => any;
  write: (rawLoanData: any, dataPath: string, value: string) => void;
  fields: AddressFieldConfig[];
};

const ADDRESS_MAPPINGS: Record<string, AddressMapping> = {
  property: {
    readPath: (loanData: LoanAppData) => loanData.getPropertyDetails().propertyAddress,
    write: (rawLoanData: any, dataPath: string, value: string) => {
      (rawLoanData.propertyAddress ??= {})[dataPath] = value || ""; },
    fields: [
      { fieldId: "property_address_line1", dataPath: "line1" },
      { fieldId: "property_address_line2", dataPath: "line2" },
      { fieldId: "property_city", dataPath: "city" },
      { fieldId: "property_zip", dataPath: "zip" },
    ],
  }
};

function copyAddressDataToField(loanData: LoanAppData, fieldId: string): string {
  for (const mapping of Object.values(ADDRESS_MAPPINGS)) {
    const fieldConfig = mapping.fields.find((f) => f.fieldId === fieldId);
    if (fieldConfig) {
      const sourceObj = mapping.readPath(loanData);
      return sourceObj?.[fieldConfig.dataPath] || "";
    }
  }
  return "";
}

function copyAddressDataFromField(loanAppData: LoanAppData, fieldId: string, value: string): void {
  const loanData = loanAppData.getRawLoanData();

  for (const mapping of Object.values(ADDRESS_MAPPINGS)) {
    const fieldConfig = mapping.fields.find((f) => f.fieldId === fieldId);
    if (fieldConfig) {
      mapping.write(loanData, fieldConfig.dataPath, value);
      return;
    }
  }
}

function setEntityInfoField(loanData: any, key: string, value: string): void {
  const trimmed = (value || "").trim();
  if (!trimmed) {
    if (loanData.entityInfo) {
      delete loanData.entityInfo[key];
      if (Object.keys(loanData.entityInfo).length === 0) {
        delete loanData.entityInfo;
      }
    }
    return;
  }

  (loanData.entityInfo ??= {})[key] = trimmed;
}

function setRawLoanDataStringField(
  rawLoanData: any,
  parentKey: string,
  fieldKey: string,
  value: string
): void {
  const parent = (rawLoanData[parentKey] ??= {});
  parent[fieldKey] = value || "";
}

type DedupeScope = {
  propertyDetails?: boolean;
  purchaseDetails?: boolean;
  projectManagement?: boolean;
};

function filterDocumentArrays(section: any) {
  if (!section) return;
  for (const [key, value] of Object.entries(section)) {
    if (
      Array.isArray(value) &&
      value.length &&
      value.every(
        (entry: any) =>
          entry &&
          typeof entry === "object" &&
          "uniqueID" in entry &&
          "fileName" in entry
      )
    ) {
      const seen = new Set<string>();
      section[key] = value.filter((doc: any) => {
        if (!doc?.uniqueID) return true;
        if (seen.has(doc.uniqueID)) return false;
        seen.add(doc.uniqueID);
        return true;
      });
    }
  }
}

// Mapping of document IDs to their storage paths in loan data
const DOCUMENT_ARRAY_PATHS: Record<string, { sectionKey: string; arrayKey: string; }> = {
  
  // 2.2 PROPERTY INFORMATION - UPLOAD DOCUMENTS
  purchase_contract_doc:      { sectionKey: 'propertyDetails',  arrayKey: 'purchaseContractDoc' },
  contract_assignment_doc:    { sectionKey: 'propertyDetails',  arrayKey: 'contractAssignmentDoc' },
  mortgage_statement_doc:     { sectionKey: 'propertyDetails',  arrayKey: 'mortgageStatementDoc' },
  property_comparables_doc:   { sectionKey: 'propertyDetails',  arrayKey: 'propertyComparablesDoc' },
  rent_roll_opex_doc:         { sectionKey: 'propertyDetails',  arrayKey: 'rentRollOpexDoc' },
  in_place_leases_doc:        { sectionKey: 'propertyDetails',  arrayKey: 'inPlaceLeasesDoc' },

  // 3.2 PROJECT INFORMATION - UPLOAD DOCUMENTS
  building_plans_doc:         { sectionKey: 'projectManagement',arrayKey: 'buildingPlans' },
  building_permit_doc:        { sectionKey: 'projectManagement',arrayKey: 'buildingPermit' },

  // 5.3 BORROWER INFORMATION - UPLOAD DOCUMENTS
  government_issued_id_doc:   { sectionKey: 'borrowerInformation',arrayKey: 'governmentIssuedIdDoc' },

  entity_formation_doc:       { sectionKey: 'borrowerInformation',arrayKey: 'entityFormationDoc' },
  irs_ein_letter_doc:         { sectionKey: 'borrowerInformation',arrayKey: 'irsEinLetterDoc' },
  operating_agreement_doc:    { sectionKey: 'borrowerInformation',arrayKey: 'operatingAgreementDoc' },
  certificate_of_good_standing_doc: { sectionKey: 'borrowerInformation',arrayKey: 'certificateOfGoodStandingDoc' },

  most_recent_bank_statement_doc: { sectionKey: 'borrowerInformation',arrayKey: 'mostRecentBankStatementDoc' },
  previous_bank_statement_doc: { sectionKey: 'borrowerInformation',arrayKey: 'previousBankStatementDoc' },

};

export const isRemovableDocumentId = (documentId: string): boolean => {
  const suffixIndex = documentId.indexOf('__');
  const baseDocId = suffixIndex === -1 ? documentId : documentId.slice(0, suffixIndex);

  return documentId.startsWith('custom_')
    || Boolean(DOCUMENT_ARRAY_PATHS[baseDocId])
    || BORROWER_INFO_DOC_IDS.has(baseDocId);
};

// Get a unique key for a document (uniqueID, blobName, fileName, or id)
function getDocumentUniqueKey(value: any): string {
  if (!value) return '';
  return String(value.uniqueID || value.blobName || value.fileName || value.id || '');
}

// Remove a document reference from the raw loan data. This removes the document from the appropriate array in loan data
// based on the documentId. Returns true if a document was removed.
export function removeDocumentFromLoanData(
  rawLoanData: any,
  documentId: string,
  docKey: string,
  docFileName?: string
): boolean {
  if (!rawLoanData) return false;
  const { baseDocId, hasSuffix, suffixNumber } = parseDocumentIdSuffix(documentId);

  // Handle custom field documents (stored in miscellaneousFields)
  if (documentId.startsWith('custom_')) {
    rawLoanData.miscellaneousFields = rawLoanData.miscellaneousFields || {};
    if (rawLoanData.miscellaneousFields[documentId] !== undefined) {
      delete rawLoanData.miscellaneousFields[documentId];
      return true;
    }
    const stripped = documentId.replace(/^custom_/, '');
    if (rawLoanData.miscellaneousFields[stripped] !== undefined) {
      delete rawLoanData.miscellaneousFields[stripped];
      return true;
    }
    return false;
  }

  // Handle documents stored in propertyDetails, purchaseDetails, or projectManagement arrays
  const mappedPath = DOCUMENT_ARRAY_PATHS[baseDocId];
  if (mappedPath) {
    const section = (rawLoanData[mappedPath.sectionKey] ||= {});
    const existing = Array.isArray(section[mappedPath.arrayKey]) ? section[mappedPath.arrayKey] : [];
    if (existing.length === 0) return false;

    if (isLiquidityStatementDocId(baseDocId)) {
      const targetIndex = hasSuffix ? (suffixNumber - 1) : 0;
      if (targetIndex < 0 || targetIndex >= existing.length) return false;

      const current = existing[targetIndex];
      const itemKey = getDocumentUniqueKey(current);
      const isMatch = (docKey && itemKey)
        ? itemKey === docKey
        : (docFileName && current?.fileName)
          ? current.fileName === docFileName
          : false;

      if (!isMatch) return false;

      const nextDocs = [...existing];
      nextDocs[targetIndex] = null;
      section[mappedPath.arrayKey] = nextDocs;
      return true;
    }

    const govtIssuedRemoval = removeGovtIssuedIdDoc(
      baseDocId,
      existing,
      { hasSuffix, suffixNumber },
      (current: any) => {
        const itemKey = getDocumentUniqueKey(current);
        return (docKey && itemKey) ? itemKey === docKey : (docFileName && current?.fileName) ? current.fileName === docFileName : false;
      }
    );
    if (govtIssuedRemoval) {
      if (!govtIssuedRemoval.removed) return false;
      section[mappedPath.arrayKey] = govtIssuedRemoval.nextDocs;
      return true;
    }

    const next = existing.filter((item: any) => {
      const itemKey = getDocumentUniqueKey(item);
      if (docKey && itemKey) return itemKey !== docKey;
      if (docFileName && item?.fileName) return item.fileName !== docFileName;
      return true;
    });
    section[mappedPath.arrayKey] = next;
    return next.length !== existing.length;
  }

  // Handle borrower info documents
  if (BORROWER_INFO_DOC_IDS.has(documentId)) {
    rawLoanData.borrowerDocuments = rawLoanData.borrowerDocuments || {};
    const existing = Array.isArray(rawLoanData.borrowerDocuments[documentId])
      ? rawLoanData.borrowerDocuments[documentId]
      : [];
    if (existing.length === 0) return false;
    const next = existing.filter((item: any) => {
      const itemKey = getDocumentUniqueKey(item);
      if (docKey && itemKey) return itemKey !== docKey;
      if (docFileName && item?.fileName) return item.fileName !== docFileName;
      return true;
    });
    rawLoanData.borrowerDocuments[documentId] = next;
    return next.length !== existing.length;
  }

  return false;
}

export function dedupeLoanDocuments(rawLoanData: any, scope: DedupeScope = {}): void {
  const {
    propertyDetails = true,
    purchaseDetails = true,
    projectManagement = true,
  } = scope;

  if (propertyDetails) {
    filterDocumentArrays(rawLoanData?.propertyDetails);
  }
  if (purchaseDetails) {
    filterDocumentArrays(rawLoanData?.purchaseDetails);
  }
  if (projectManagement) {
    filterDocumentArrays(rawLoanData?.projectManagement);
  }
}

function pickLast<T>(arr?: T[]): T | undefined {
  return Array.isArray(arr) && arr.length > 0 ? arr[arr.length - 1] : undefined;
}

function safePush(
  obj: any,
  sectionKey: string,
  arrayKey: string,
  document: DocumentUploaded
) {
  if (!obj) return;
  obj[sectionKey] = obj[sectionKey] || {};
  const section = obj[sectionKey];
  const arr = (section[arrayKey] = Array.isArray(section[arrayKey])
    ? section[arrayKey]
    : []);
  const seen = new Set<string>();
  section[arrayKey] = arr.filter((doc: any) => {
    const uid = (doc as any)?.uniqueID as string | undefined;
    if (!uid) return true;
    if (seen.has(uid)) return false;
    seen.add(uid);
    return true;
  });
  // push if not duplicate uniqueID
  const newUid = (document as any)?.uniqueID as string | undefined;
  if (!newUid || !seen.has(newUid)) {
    section[arrayKey].push(document);
  }
}

function safePushBorrowerDocument(
  obj: any,
  docId: string,
  document: DocumentUploaded
) {
  if (!obj) return;
  obj.borrowerDocuments = obj.borrowerDocuments || {};
  const arr = (obj.borrowerDocuments[docId] = Array.isArray(
    obj.borrowerDocuments[docId]
  )
    ? obj.borrowerDocuments[docId]
    : []);
  const seen = new Set<string>();
  obj.borrowerDocuments[docId] = arr.filter((doc: any) => {
    const uid = (doc as any)?.uniqueID as string | undefined;
    if (!uid) return true;
    if (seen.has(uid)) return false;
    seen.add(uid);
    return true;
  });
  const newUid = (document as any)?.uniqueID as string | undefined;
  if (!newUid || !seen.has(newUid)) {
    obj.borrowerDocuments[docId].push(document);
  }
}

export function copySOWToLoanData(
  loanAppData: LoanAppData,
  sowData: SOWData
): void {
  const loanData = loanAppData.getRawLoanData() as any;
  loanData.projectSummary ??= {};
  const normalized = normalizeSOWData(sowData ?? initializeSOWData());
  loanData.projectSummary.scopeOfWork = filterSOWData(normalized);
}
